import React, { useContext, useState } from "react";
import { Checkbox, Flex } from "@fluentui/react-northstar";
import { Loader } from "ecom-teams-apps-core";
import { CustomersSection, OtherParametersSection } from "../reportSection";
import { useQuery } from "react-query";
import { SalesOverviewSection } from "../reportSection/sales-overview/SalesOverviewSection";
import { IntermedaryFilter } from "../reportSection/filters/IntermedaryFilter";
import { IntermediaryNumber, ReportDate, Scope } from "../reportSection/models";
import styles from "./view360Reports.module.scss";
import { SearchEntityTypeEnum } from "view360Card/models";
import { SegmentContent } from "shared/components";
import {
    SalesAndActivitySection,
    SalesAndActivitySectionDataSource,
} from "../reportSection/sales-and-activity/SalesAndActivitySection";
import pl from "shared/messages/pl";
import FailedToFetchReportError from "../reportSection/error/FailedToFetchReportError";
import ReportsConfigService from "../reportSection/service/ReportsConfigService";
import { View360Context } from "view360Card/store";
import { formatDate } from "utils/formattingUtils";
import { constants } from "shared/constants";
import { DailySalesSection } from "../reportSection/daily-sales/DailySalesSection";
import { AuthContext } from "authorization/AuthProvider";
import { GrantEnum } from "view360Card/models/GrantEnum";

export interface View360ReportsProps {
    entityId: number;
    entityType: SearchEntityTypeEnum;
    isMobileView: boolean;
}

export const View360Reports: React.FC<View360ReportsProps> = ({ entityId, entityType, isMobileView }) => {
    const [scope, setScope] = useState<Scope>();
    const [intermediaryNumber, setIntermediaryNumber] = useState<IntermediaryNumber>();
    const view360Context = useContext(View360Context);
    const authContext = useContext(AuthContext);

    const getReportDatesText = (reportDate: ReportDate[]) => {
        return reportDate
            .map((r) => pl.view360Card.reports.reportDate(r.companyName, formatDate(r.timestamp) ?? constants.noValue))
            .join(` ${constants.inlineSeparator} `);
    };

    const { isFetching, isError, data } = useQuery("reportConfig", async () => {
        const config = await ReportsConfigService.getReportConfig(entityId, entityType);
        if (config.scopeList.length > 0) {
            setScope(config.scopeList[0]);
        }
        view360Context.setCardHeaderSubtitle(getReportDatesText(config.reportDateList));

        return config;
    });

    if (isFetching) {
        return (
            <SegmentContent>
                <div className={styles.loaderWrapper}>
                    <Loader text={pl.view360Card.reports.fetchingReportsConfiguration} />
                </div>
            </SegmentContent>
        );
    }

    if (isError) {
        return (
            <FailedToFetchReportError
                message={pl.view360Card.reports.errors.failedToFetchReportsConfigurationErrorMessage}
            />
        );
    }

    if (!data) {
        return <FailedToFetchReportError message={pl.view360Card.reports.errors.noReportsConfiguration} />;
    }

    const getGlobalFilters = () => (
        <Flex column={isMobileView} space="between" className={styles.globalFilters}>
            {data.intermediaryList && data.intermediaryList.length > 0 ? (
                <IntermedaryFilter
                    intermediaryList={data.intermediaryList}
                    onSelectedIntermediaryChanged={(intermediaryList: IntermediaryNumber[]) =>
                        setIntermediaryNumber(intermediaryList.find((i) => i.selected))
                    }
                    isMobileView={isMobileView}
                />
            ) : (
                <div />
            )}
            {data.scopeList.length > 1 && (
                <Flex hAlign="end">
                    <Checkbox
                        label={data.scopeList[0].content}
                        checked={scope?.selected}
                        toggle
                        onChange={(e, p) => {
                            if (p?.checked) {
                                setScope(data.scopeList[0]);
                            } else {
                                setScope(data.scopeList[1]);
                            }
                        }}
                    />
                </Flex>
            )}
        </Flex>
    );

    return (
        <Flex column gap="gap.medium">
            {getGlobalFilters()}

            {authContext.isUserGrantedTo(GrantEnum.V360SalesDaily) && (
                <DailySalesSection entityId={entityId} entityType={entityType} isMobileView={isMobileView} />
            )}

            {data.salesOverviewSectionConfigList.map((sectionConfig) => {
                return (
                    <SalesOverviewSection
                        key={`${entityId}${sectionConfig.company.code}`}
                        entityId={entityId}
                        entityType={entityType}
                        intermediaryNumber={intermediaryNumber}
                        scope={scope}
                        isMobileView={isMobileView}
                        sectionConfig={sectionConfig}
                    />
                );
            })}

            {data.salesAndActivitySectionConfigList.map((sectionConfig) => {
                return (
                    <SalesAndActivitySection
                        key={`sales_activity_${entityId}${sectionConfig.company.code}`}
                        dataSource={
                            entityType === SearchEntityTypeEnum.SalesPerson
                                ? SalesAndActivitySectionDataSource.salesPerson
                                : SalesAndActivitySectionDataSource.intermediary
                        }
                        id={entityId}
                        salesPersonIntermediaryNumber={intermediaryNumber?.code}
                        scope={scope}
                        isMobileView={isMobileView}
                        sectionConfig={sectionConfig}
                    />
                );
            })}

            {entityType === SearchEntityTypeEnum.Intermediary && (
                <>
                    {data.customersSectionConfigList.map((sectionConfig) => {
                        return (
                            <CustomersSection
                                key={`customers_${entityId}${sectionConfig.company.code}`}
                                intermediaryId={entityId}
                                scope={scope}
                                isMobileView={isMobileView}
                                sectionConfig={sectionConfig}
                            />
                        );
                    })}

                    {data.otherParametersSectionConfigList.map((sectionConfig) => {
                        return (
                            <OtherParametersSection
                                key={`other_parameters_${entityId}${sectionConfig.company.code}`}
                                intermediaryId={entityId}
                                scope={scope}
                                isMobileView={isMobileView}
                                sectionConfig={sectionConfig}
                            />
                        );
                    })}
                </>
            )}
        </Flex>
    );
};
